import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useBooking } from "@/context/BookingContext";
import { useContent } from "@/context/ContentContext";

const ease = [0.16, 1, 0.3, 1];

export const Hero = () => {
  const { openTour, openCafe } = useBooking();
  const { content } = useContent();
  const HERO_SLIDES = content.hero_slides;
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % HERO_SLIDES.length), 5500);
    return () => clearInterval(t);
  }, [HERO_SLIDES.length]);

  const slide = HERO_SLIDES[index] || HERO_SLIDES[0];

  return (
    <section id="top" data-testid="hero" className="relative h-screen w-full overflow-hidden bg-[#23281f]">
      <AnimatePresence>
        <motion.div
          key={index}
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ opacity: { duration: 1.4, ease }, scale: { duration: 6, ease: "linear" } }}
          className="absolute inset-0"
        >
          <img src={slide.image} alt={slide.caption} style={{ objectPosition: slide.focal }} className="h-full w-full object-cover" />
        </motion.div>
      </AnimatePresence>

      <div className="absolute inset-0 bg-[#12160f]/45" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0d110d] via-[#0d110d]/25 to-[#0d110d]/55" />

      <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-6 pb-24 lg:px-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial="hidden"
            animate="show"
            exit={{ opacity: 0, transition: { duration: 0.4 } }}
          >
            <motion.div
              variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }}
              transition={{ duration: 0.8, ease, delay: 0.2 }}
              className="mb-6 inline-block rounded-none bg-black/45 px-3 py-1.5 backdrop-blur-sm"
            >
              <span className="overline !text-[#f2e4c9]">{slide.caption}</span>
            </motion.div>

            <h1 className="font-serif text-[13vw] font-medium leading-[0.92] tracking-tight text-[#f6f3ec] text-shadow-soft sm:text-[9vw] lg:text-[7.5rem]">
              <span className="block overflow-hidden">
                <motion.span
                  variants={{ hidden: { y: "110%" }, show: { y: "0%" } }}
                  transition={{ duration: 1, ease, delay: 0.25 }}
                  className="block"
                >
                  {slide.line1}
                </motion.span>
              </span>
              <span className="block overflow-hidden">
                <motion.span
                  variants={{ hidden: { y: "110%" }, show: { y: "0%" } }}
                  transition={{ duration: 1, ease, delay: 0.4 }}
                  className="block italic text-[#e7d2ad]"
                >
                  {slide.line2}
                </motion.span>
              </span>
            </h1>
          </motion.div>
        </AnimatePresence>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.9 }}
          className="mt-8 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between"
        >
          <p className="max-w-md text-base leading-relaxed text-[#e4e2da] text-shadow-soft">
            Treks, backcountry expeditions, climbing, yoga, culture and mountain music — with a
            farm-to-table cafe and stay waiting on the trail to Kedarkantha.
          </p>
          <div className="flex flex-wrap gap-4">
            <button
              data-testid="hero-explore-btn"
              onClick={() => openTour()}
              className="inline-flex items-center rounded-none bg-[#ba5a45] px-8 py-4 text-xs font-semibold uppercase tracking-widest text-[#f6f3ec] transition-colors hover:bg-[#9c4633]"
            >
              Explore Experiences
            </button>
            <button
              data-testid="hero-reserve-btn"
              onClick={openCafe}
              className="inline-flex items-center rounded-none border border-[#f6f3ec]/50 px-8 py-4 text-xs font-semibold uppercase tracking-widest text-[#f6f3ec] transition-colors hover:bg-[#f6f3ec]/10"
            >
              Reserve a Table
            </button>
          </div>
        </motion.div>

        <div className="mt-10 flex gap-2">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              aria-label={`slide ${i + 1}`}
              onClick={() => setIndex(i)}
              className={`h-[3px] w-10 transition-all duration-500 ${
                i === index ? "bg-[#e7d2ad]" : "bg-[#f6f3ec]/30"
              }`}
            />
          ))}
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2 text-[#e4e2da]">
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown className="h-4 w-4 animate-bounce" />
      </div>
    </section>
  );
};
